import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PlusCircle, Download, Filter, X, Edit, Trash2 } from 'lucide-react';
import { biz } from '../../lib/api';
import { StatusBadge, PriorityBadge } from './BusinessLayout';

const initialForm = {
  date: new Date().toISOString().slice(0, 10),
  time: new Date().toTimeString().slice(0, 5),
  employee_name: '', department: 'Sales', activity_type: 'Customer Call',
  description: '', customer_name: '', contact_number: '', location: '',
  complaint_number: '', order_number: '', invoice_number: '',
  priority: 'medium', status: 'pending',
  amount: 0, expense: 0,
  followup_required: false, followup_date: '', followup_person: '',
  remarks: '',
};

export default function DayBook() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState({ date_from: '', date_to: '', activity_type: '', status: '', priority: '', q: '' });
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = { ...filter };
      Object.keys(q).forEach((k) => !q[k] && delete q[k]);
      const [r, t] = await Promise.all([biz.listDaybook(q), biz.listActivityTypes()]);
      setRows(r); setTypes(t);
    } catch (e) {
      toast.error('Failed to load Day Book');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get('new') === '1') { setShowForm(true); params.delete('new'); setParams(params); }
  }, [params, setParams]);

  const save = async () => {
    if (!form.description || !form.employee_name || !form.activity_type) {
      toast.error('Employee, activity type & description are required'); return;
    }
    try {
      if (editing) {
        await biz.patchDaybook(editing.id, form);
        toast.success('Activity updated');
      } else {
        const created = await biz.createDaybook({
          ...form,
          amount: Number(form.amount || 0), expense: Number(form.expense || 0),
        });
        toast.success(`Activity ${created.activity_number} added`);
      }
      setShowForm(false); setEditing(null); setForm(initialForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    try { await biz.delDaybook(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed (admin only)'); }
  };

  return (
    <div className="grid gap-3" data-testid="biz-daybook-root">
      <div className="flex items-center gap-2 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-black text-[#D35400]">Day Book</div>
          <h1 className="font-heading font-black text-2xl md:text-3xl">Daily Activity Log</h1>
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <a href={biz.exportDaybookCsvUrl(filter)} className="pill !bg-white text-xs" data-testid="biz-daybook-export">
            <Download className="w-3 h-3"/>Export CSV
          </a>
          <button className="btn-primary" onClick={() => { setEditing(null); setForm(initialForm); setShowForm(true); }}
            data-testid="biz-daybook-new-btn">
            <PlusCircle className="w-4 h-4"/> New Activity
          </button>
        </div>
      </div>

      <div className="card-soft p-3 flex flex-wrap gap-2 items-end text-xs" data-testid="biz-daybook-filters">
        <F label="From"><input type="date" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })}/></F>
        <F label="To"><input type="date" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })}/></F>
        <F label="Activity type">
          <select value={filter.activity_type} onChange={(e) => setFilter({ ...filter, activity_type: e.target.value })}>
            <option value="">All</option>
            {types.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
        </F>
        <F label="Status">
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
            <option value="">All</option>
            <option value="pending">Pending</option><option value="in_progress">In progress</option>
            <option value="completed">Completed</option><option value="cancelled">Cancelled</option>
          </select>
        </F>
        <F label="Priority">
          <select value={filter.priority} onChange={(e) => setFilter({ ...filter, priority: e.target.value })}>
            <option value="">All</option><option value="critical">Critical</option>
            <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
        </F>
        <F label="Search"><input placeholder="Customer, ACT no…" value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}/></F>
      </div>

      <div className="card-soft overflow-x-auto" data-testid="biz-daybook-table">
        <table className="min-w-full text-sm">
          <thead className="bg-[#FFF8EE] text-[10px] uppercase tracking-widest text-ink/60">
            <tr>
              {['Activity #', 'Date', 'Type', 'Employee', 'Customer', 'Priority', 'Status', 'Amount', ''].map((h) =>
                <th key={h} className="text-left px-2 py-2 whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="9" className="p-4 text-ink/50">Loading…</td></tr>
              : !rows.length ? <tr><td colSpan="9" className="p-4 text-ink/50">No activities.</td></tr>
                : rows.map((r) => (
                  <tr key={r.id} className="border-t border-border/60" data-testid={`biz-daybook-row-${r.activity_number}`}>
                    <td className="px-2 py-1.5 font-mono text-xs">{r.activity_number}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{r.date} {r.time}</td>
                    <td className="px-2 py-1.5">{r.activity_type}</td>
                    <td className="px-2 py-1.5">{r.employee_name}</td>
                    <td className="px-2 py-1.5">
                      <div>{r.customer_name || '—'}</div>
                      {r.contact_number && <div className="text-[10px] text-ink/50">{r.contact_number}</div>}
                    </td>
                    <td className="px-2 py-1.5"><PriorityBadge value={r.priority}/></td>
                    <td className="px-2 py-1.5"><StatusBadge value={r.status}/></td>
                    <td className="px-2 py-1.5 tabular-nums">₹{Number(r.amount || 0).toLocaleString('en-IN')}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <button className="pill !bg-white text-[10px] mr-1" onClick={() => { setEditing(r); setForm({ ...initialForm, ...r }); setShowForm(true); }}
                        data-testid={`biz-daybook-edit-${r.activity_number}`}>
                        <Edit className="w-3 h-3"/>Edit
                      </button>
                      <button className="pill !bg-white !text-[#B71C1C] text-[10px]" onClick={() => del(r.id)}
                        data-testid={`biz-daybook-delete-${r.activity_number}`}>
                        <Trash2 className="w-3 h-3"/>
                      </button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <FormModal
          form={form} setForm={setForm} onSave={save} onClose={() => { setShowForm(false); setEditing(null); }}
          types={types} editing={editing}/>
      )}
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="uppercase tracking-widest text-[10px] text-ink/50">{label}</span>
      <span className="[&>*]:rounded-lg [&>*]:border [&>*]:border-border [&>*]:px-2 [&>*]:py-1.5 [&>*]:bg-white">{children}</span>
    </label>
  );
}

function FormModal({ form, setForm, onSave, onClose, types, editing }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-2" data-testid="biz-daybook-modal" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white rounded-2xl border border-border shadow-xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-heading font-bold">{editing ? `Edit activity ${editing.activity_number}` : 'New Activity'}</div>
          <button onClick={onClose} data-testid="biz-daybook-modal-close"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <L label="Date"><input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} data-testid="biz-form-date"/></L>
          <L label="Time"><input type="time" value={form.time} onChange={(e) => set('time', e.target.value)} data-testid="biz-form-time"/></L>
          <L label="Employee"><input value={form.employee_name} onChange={(e) => set('employee_name', e.target.value)} data-testid="biz-form-employee"/></L>
          <L label="Department"><input value={form.department} onChange={(e) => set('department', e.target.value)} data-testid="biz-form-dept"/></L>
          <L label="Activity type">
            <select value={form.activity_type} onChange={(e) => set('activity_type', e.target.value)} data-testid="biz-form-type">
              {types.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          </L>
          <L label="Priority">
            <select value={form.priority} onChange={(e) => set('priority', e.target.value)} data-testid="biz-form-priority">
              <option value="critical">Critical</option><option value="high">High</option>
              <option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </L>
          <L label="Status">
            <select value={form.status} onChange={(e) => set('status', e.target.value)} data-testid="biz-form-status">
              <option value="pending">Pending</option><option value="in_progress">In progress</option>
              <option value="completed">Completed</option><option value="cancelled">Cancelled</option>
            </select>
          </L>
          <L label="Amount (₹)"><input type="number" value={form.amount} onChange={(e) => set('amount', e.target.value)} data-testid="biz-form-amount"/></L>
          <L label="Expense (₹)"><input type="number" value={form.expense} onChange={(e) => set('expense', e.target.value)} data-testid="biz-form-expense"/></L>
          <L label="Customer name"><input value={form.customer_name || ''} onChange={(e) => set('customer_name', e.target.value)} data-testid="biz-form-customer"/></L>
          <L label="Contact"><input value={form.contact_number || ''} onChange={(e) => set('contact_number', e.target.value)} data-testid="biz-form-contact"/></L>
          <L label="Location"><input value={form.location || ''} onChange={(e) => set('location', e.target.value)} data-testid="biz-form-location"/></L>
          <L label="Complaint #"><input value={form.complaint_number || ''} onChange={(e) => set('complaint_number', e.target.value)} data-testid="biz-form-complaint"/></L>
          <L label="Order #"><input value={form.order_number || ''} onChange={(e) => set('order_number', e.target.value)} data-testid="biz-form-order"/></L>
          <L label="Invoice #"><input value={form.invoice_number || ''} onChange={(e) => set('invoice_number', e.target.value)} data-testid="biz-form-invoice"/></L>
          <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
            <label className="inline-flex items-center gap-2 mt-2">
              <input type="checkbox" checked={form.followup_required} onChange={(e) => set('followup_required', e.target.checked)}
                data-testid="biz-form-followup-flag"/>
              Follow-up required?
            </label>
            {form.followup_required && (
              <>
                <L label="Follow-up date"><input type="date" value={form.followup_date || ''} onChange={(e) => set('followup_date', e.target.value)} data-testid="biz-form-followup-date"/></L>
                <L label="Follow-up person"><input value={form.followup_person || ''} onChange={(e) => set('followup_person', e.target.value)} data-testid="biz-form-followup-person"/></L>
              </>
            )}
          </div>
          <div className="col-span-full">
            <L label="Description">
              <textarea rows="2" value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="biz-form-description"/>
            </L>
          </div>
          <div className="col-span-full">
            <L label="Remarks"><textarea rows="1" value={form.remarks || ''} onChange={(e) => set('remarks', e.target.value)} data-testid="biz-form-remarks"/></L>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button className="pill !bg-white" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={onSave} data-testid="biz-form-save">
            {editing ? 'Save changes' : 'Add activity'}
          </button>
        </div>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-widest text-ink/50">{label}</span>
      <span className="[&>*]:w-full [&>*]:rounded-lg [&>*]:border [&>*]:border-border [&>*]:px-2 [&>*]:py-1.5 [&>*]:bg-white">{children}</span>
    </label>
  );
}
